/* eslint-disable */
/**
 * RankingChart — Bar chart showing which asset contributed most to portfolio
 * 
 * Props:
 *   assetData: array of { ticker, totalDollars, currentValue, pnl, pnlPct, livePrice }
 * 
 * Usage in PortfolioPage:
 *   import RankingChart from "./components/charts/RankingChart.jsx";
 *   <RankingChart assetData={assetData} />
 */
import { useState } from "react";
import { C, fmt$ } from "../shared/globals.js";

const fmtPct = v => (v >= 0 ? "+" : "") + v.toFixed(2) + "%";

const METRICS = [
  { key:"pnl",          label:"P&L $",        fmt: v => fmt$(v) },
  { key:"pnlPct",       label:"P&L %",        fmt: v => fmtPct(v) },
  { key:"currentValue", label:"Current Value", fmt: v => fmt$(v) },
  { key:"totalDollars", label:"Invested",      fmt: v => fmt$(v) },
];

export default function RankingChart({ assetData = [] }) {
  const [metric, setMetric] = useState("pnl");

  if (!assetData.length) return null;

  const m = METRICS.find(x => x.key === metric);
  const sorted = [...assetData]
    .map(a => ({ ...a, _val: a[metric] ?? 0 }))
    .sort((a, b) => b._val - a._val);

  const maxAbs = Math.max(...sorted.map(a => Math.abs(a._val)), 1);

  return (
    <div style={{ fontFamily:"Montserrat,sans-serif" }}>
      {/* Metric selector */}
      <div style={{ display:"flex", gap:6, marginBottom:20, flexWrap:"wrap" }}>
        {METRICS.map(mx => {
          const active = metric === mx.key;
          return (
            <button key={mx.key} onClick={() => setMetric(mx.key)}
              style={{
                background: active ? "rgba(242,135,5,0.15)" : "transparent",
                border: `1px solid ${active ? C.orange : "var(--border)"}`,
                borderRadius:6, padding:"4px 12px", cursor:"pointer",
                fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700,
                letterSpacing:"0.12em", color: active ? C.orange : "var(--text4)",
                transition:"all 0.15s",
              }}>
              {mx.label}
            </button>
          );
        })}
      </div>

      {/* Bars */}
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {sorted.map((a, i) => {
          const val    = a._val;
          const pct    = Math.abs(val) / maxAbs * 100;
          const isPos  = val >= 0;
          const color  = metric === "pnl" || metric === "pnlPct"
            ? (isPos ? C.green : C.red)
            : C.orange;

          return (
            <div key={a.id || a.ticker}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:8, fontWeight:700, color:"var(--text4)", width:16, textAlign:"right" }}>#{i+1}</span>
                  <span style={{ fontSize:13, fontWeight:900, color:C.orange }}>{a.ticker}</span>
                </div>
                <span style={{ fontSize:12, fontWeight:700, color }}>
                  {m.fmt(val)}
                </span>
              </div>
              <div style={{ height:8, background:"var(--bg3)", borderRadius:4, overflow:"hidden" }}>
                <div style={{
                  height:"100%",
                  width:`${pct}%`,
                  background: color,
                  borderRadius:4,
                  transition:"width 0.5s cubic-bezier(0.4,0,0.2,1)",
                  opacity: 0.85,
                }}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
